// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Turning things on the plan.
 *
 * Almost nothing on a site is square to the sheet. A 10'x45' field office sits
 * along whatever edge of the lot it fits, a dumpster backs up to the building
 * at whatever angle the drive allows, a laydown area follows the property
 * line. Drawn only axis-aligned, a trailer that is really at 30 degrees either
 * lies about where it is or lies about how much room it leaves — and the
 * drawing exists to answer exactly that.
 *
 * The one trap here is the coordinate space. Positions are stored as a percent
 * of the canvas width and a percent of its height, independently, and the
 * canvas is almost never square. Rotating those percentages directly shears
 * the shape: a square comes out a parallelogram, and a trailer's 10' side
 * stops being 10'. So every rotation converts to a space where one unit means
 * the same distance on both axes, turns, and converts back.
 */

export interface Point { x: number; y: number }

/** Degrees between the stops when a rotation is snapped. */
export const ROTATE_SNAP = 15;

const RAD = Math.PI / 180;

/**
 * The aspect correction for a canvas: how many x-percent one y-percent spans.
 *
 * The plan image keeps its own proportions, so a percent of height is a
 * different real distance from a percent of width unless the sheet is square.
 */
export function aspectOf(canvas: { w: number; h: number } | null | undefined): number {
  if (!canvas?.w || !canvas?.h) return 1;
  if (!Number.isFinite(canvas.w) || !Number.isFinite(canvas.h)) return 1;
  return canvas.h / canvas.w;
}

/** An angle brought back into 0–360, so stored values stay comparable. */
export function normalizeAngle(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  return ((deg % 360) + 360) % 360;
}

/** The nearest stop, for a rotation dragged with shift held. */
export function snapAngle(deg: number, step = ROTATE_SNAP): number {
  if (!Number.isFinite(deg)) return 0;
  if (!step || step <= 0) return normalizeAngle(deg);
  return normalizeAngle(Math.round(deg / step) * step);
}

/** The middle of a set of points. */
export function centroid(points: Point[]): Point {
  if (!points?.length) return { x: 0, y: 0 };
  let x = 0, y = 0;
  for (const p of points) { x += p.x; y += p.y; }
  return { x: x / points.length, y: y / points.length };
}

/**
 * One point turned about another.
 *
 * `aspect` is what keeps the turn a turn rather than a shear: the y leg is
 * scaled into x-units before rotating and back again afterwards.
 */
export function rotatePoint(p: Point, about: Point, deg: number, aspect = 1): Point {
  const a = normalizeAngle(deg) * RAD;
  const cos = Math.cos(a), sin = Math.sin(a);
  const dx = p.x - about.x;
  const dy = (p.y - about.y) * aspect;
  return {
    x: about.x + dx * cos - dy * sin,
    y: about.y + (dx * sin + dy * cos) / aspect,
  };
}

/**
 * A whole shape turned, about its own middle unless told otherwise.
 *
 * Any other fields a vertex carries — whether it is a smooth point, for
 * instance — come through untouched. Rotating a shape must not quietly
 * straighten its corners.
 */
export function rotatePoints<T extends Point>(
  points: T[], deg: number, aspect = 1, about?: Point,
): T[] {
  if (!points?.length) return points || [];
  const pivot = about ?? centroid(points);
  return points.map((p) => {
    const r = rotatePoint(p, pivot, deg, aspect);
    return { ...p, x: r.x, y: r.y };
  });
}

/**
 * The angle a rotation handle has been dragged to.
 *
 * Zero is straight up, and the angle grows clockwise, because that is how a
 * handle above a shape reads and how every bearing on a site plan is written.
 */
export function angleFromPointer(center: Point, pointer: Point, aspect = 1): number {
  const dx = pointer.x - center.x;
  const dy = (pointer.y - center.y) * aspect;
  if (dx === 0 && dy === 0) return 0;
  return normalizeAngle(Math.atan2(dx, -dy) / RAD);
}

/**
 * Where a rotation handle sits: directly "above" the shape once its own
 * rotation is taken into account, so the handle travels with what it turns.
 */
export function handleAt(center: Point, distance: number, deg: number, aspect = 1): Point {
  const a = normalizeAngle(deg) * RAD;
  return {
    x: center.x + Math.sin(a) * distance,
    y: center.y - (Math.cos(a) * distance) / aspect,
  };
}

/** A readable bearing for a label or a field: "30°", "0°" for none. */
export function formatAngle(deg: number): string {
  return `${Math.round(normalizeAngle(deg))}°`;
}

/** Whether a shape is turned at all, for hiding the readout when it is not. */
export function isRotated(deg: number | null | undefined): boolean {
  return !!deg && Math.abs(normalizeAngle(deg)) > 0.01 && Math.abs(normalizeAngle(deg) - 360) > 0.01;
}


/**
 * A shape scaled about a point.
 *
 * No aspect correction, and that is not an oversight. Points are kept as a
 * percentage of the width and a percentage of the height independently, so a
 * percentage offset on each axis already means the same fraction of that
 * axis: multiply both by k and both pixel offsets grow by k, whatever shape
 * the canvas is. Rotation is the one that has to convert, because it mixes
 * the axes together; scaling never does.
 *
 * The factor is clamped so a shape cannot be scaled to nothing and lost, or
 * blown so far off the sheet that it cannot be found to undo.
 */
export function scalePoints<P extends { x: number; y: number }>(
  points: P[],
  factor: number,
  origin: { x: number; y: number },
): P[] {
  const pts = points || [];
  if (!pts.length) return pts;
  if (!Number.isFinite(factor)) return pts.map((p) => ({ ...p }));
  const k = Math.max(0.05, Math.min(20, factor));
  return pts.map((p) => ({
    ...p,
    x: origin.x + (p.x - origin.x) * k,
    y: origin.y + (p.y - origin.y) * k,
  }));
}


/** The box a shape sits in. */
export interface Bounds { x: number; y: number; w: number; h: number }

/**
 * The bounding box of a run of points.
 *
 * What a selection is drawn around, and what its corner grips hang off.
 * A shape is a list of vertices; the box is the thing people expect to grab
 * to make the whole of it bigger.
 */
export function boundsOf(points: { x: number; y: number }[]): Bounds | null {
  const pts = (points || []).filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y));
  if (!pts.length) return null;
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** The four corners, named the way a resize cursor is. */
export type Corner = "nw" | "ne" | "se" | "sw";

export function cornerPoint(b: Bounds, c: Corner): { x: number; y: number } {
  return {
    x: c === "nw" || c === "sw" ? b.x : b.x + b.w,
    y: c === "nw" || c === "ne" ? b.y : b.y + b.h,
  };
}

/** The corner diagonally across from this one, which is what stays put
 *  while the other is dragged. */
export function oppositeCorner(c: Corner): Corner {
  return c === "nw" ? "se" : c === "se" ? "nw" : c === "ne" ? "sw" : "ne";
}
